import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import DocDetail from "@/components/DocDetail";

export default function HistoryPanel({ organId = "" }) {
  const { companyId } = useCompany();
  const [status, setStatus] = useState(null), [coverage, setCoverage] = useState(null);
  const [results, setResults] = useState(null), [year, setYear] = useState("");
  const [offset, setOffset] = useState(0), [detail, setDetail] = useState(null), [error, setError] = useState("");
  const generation = useRef(0);
  useEffect(() => { setDetail(null); setOffset(0); setResults(null); setStatus(null); setCoverage(null); setError(""); }, [companyId, organId, year]);
  useEffect(() => {
    const request = ++generation.current, controller = new AbortController();
    if (!companyId) return () => controller.abort();
    const config = { signal: controller.signal, headers: { "x-company-id": companyId } };
    const root = `/controller/v1/companies/${encodeURIComponent(companyId)}/history`;
    const load = async () => {
      try {
        const params = new URLSearchParams({ offset: String(offset), limit: "15", organ: organId });
        if (year) params.set("year", year);
        const [s,c,r] = await Promise.all([api.get(`${root}/status`,config),api.get(`${root}/coverage`,config),api.get(`${root}/results?${params}`,config)]);
        if (controller.signal.aborted || request !== generation.current) return;
        if ([s,c,r].some(x => x.data.company_id !== companyId)) throw new Error("Isolamento aziendale: risposta rifiutata");
        setStatus(s.data); setCoverage(c.data); setResults(r.data); setError("");
      } catch(e) { if (!controller.signal.aborted && request === generation.current) setError("Lettura dello storico non disponibile. Nessuna completezza dedotta."); }
    };
    load(); const timer = setInterval(load, 20000);
    return () => { generation.current += 1; clearInterval(timer); controller.abort(); };
  }, [companyId, organId, year, offset]);
  const control = async action => {
    const request = generation.current;
    try {
      await api.post(`/controller/v1/companies/${encodeURIComponent(companyId)}/history/control`,
        { action, idempotency_key: `dashboard-${action}-${Date.now()}` }, { headers: { "x-company-id": companyId } });
      if (request === generation.current) setStatus(prev => ({...prev,run:{...prev?.run,desired_state:action === "pause" ? "PAUSED":"RUNNING"}}));
    } catch { if (request === generation.current) setError("Comando non accettato: verificare ruolo, grants e preparazione del censimento."); }
  };
  const open = async id => {
    const request = generation.current;
    try {
      const {data} = await api.get(`/documents/${encodeURIComponent(id)}`,{headers:{"x-company-id":companyId}});
      if (request === generation.current && data.company_id === companyId) setDetail(data);
    } catch { if (request === generation.current) setError("Documento non disponibile nel perimetro aziendale corrente."); }
  };
  return <section className="glass rounded-2xl p-4 my-5" data-testid="history-panel">
    <h2 className="text-lg font-semibold">Storico aziendale · proposte da revisionare</h2>
    <p className="text-sm text-amber-300">Copertura parziale. Analisi locale distinta dalla lettura AI. Nessuna contabilizzazione definitiva. HR/payroll richiedono grants dedicati.</p>
    {error && <p role="alert">{error}</p>}
    <div className="flex flex-wrap gap-3 my-3">
      <span>{status?.run?.state || "Stato non ancora disponibile"} · richiesta: {status?.run?.desired_state || "—"}</span>
      <button className="chip" onClick={() => control("start")}>Avvia storico</button>
      <button className="chip" onClick={() => control("pause")}>Pausa</button>
      <button className="chip" onClick={() => control("resume")}>Riprendi</button>
      <label>Anno candidato <select aria-label="Anno storico" value={year} onChange={e=>setYear(e.target.value)} className="bg-slate-900">
        <option value="">Tutti / non assegnati</option>
        {(coverage?.years || [{year:2024},{year:2025},{year:2026}]).map(x=><option key={x.year} value={x.year}>{x.year}</option>)}
      </select></label>
    </div>
    <p className="text-sm">Grant: {status?.run?.grant_expires_at || "non verificato"} · Senza anno univoco: {coverage?.unassigned_year ?? "—"}</p>
    <div className="flex flex-wrap gap-3 text-xs my-2">{Object.entries(status?.item_states || {}).map(([state,count])=><span key={state}>{state}: {count}</span>)}</div>
    <ul className="divide-y divide-slate-700">{(results?.items || []).map(doc=><li key={doc.doc_id} className="py-2">
      <button className="text-left text-cyan-200" onClick={()=>open(doc.doc_id)}>{doc.filename}</button>
      <p className="text-xs">{doc.review_state || "NEEDS_REVIEW"} · {doc.derived_analysis?.candidate || doc.doc_type || "UNKNOWN"} · {(doc.derived_analysis?.year_candidates || []).join(", ") || "anno da assegnare"}</p>
      <p className="text-xs break-all">{doc.source_provenance?.source}:{doc.source_provenance?.source_id} · versione {doc.source_provenance?.source_version}</p>
    </li>)}</ul>
    <div className="flex gap-3 mt-3"><button disabled={!offset} onClick={()=>setOffset(Math.max(0,offset-15))}>Precedenti</button><span>{results?.total ?? "—"} documenti nel filtro</span><button disabled={!results?.has_more} onClick={()=>setOffset(offset+15)}>Successivi</button></div>
    {detail && detail.company_id === companyId && <DocDetail key={`${companyId}:${detail.doc_id}`} detail={detail} onClose={()=>setDetail(null)} onUpdate={()=>setDetail(null)}/>}
  </section>;
}
